/**
 * Shared mobile-first UI primitives.
 *
 * Deliberately small and unabstracted — a handful of components used everywhere
 * beats a component library nobody remembers the API of. Every interactive
 * element carries `tap-target` (44px minimum) because the primary device here is
 * a phone held one-handed.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { formatINR, type Paise } from '../lib/money'

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

// --- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-brand-700 text-white hover:bg-brand-800 active:bg-brand-900',
  secondary: 'bg-white text-brand-800 border border-stone-300 hover:bg-stone-50',
  ghost: 'bg-transparent text-brand-800 hover:bg-brand-50',
  danger: 'bg-red-700 text-white hover:bg-red-800',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  /** Fills the width — the right default for a stacked mobile form. */
  block?: boolean
  loading?: boolean
}

export function Button({
  variant = 'primary',
  block,
  loading,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'tap-target inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3',
        'text-base font-semibold transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        buttonStyles[variant],
        block && 'w-full',
        className,
      )}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  )
}

// --- Card -------------------------------------------------------------------

export function Card({
  children,
  className,
  as: As = 'div',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'section' | 'li' | 'article'
}) {
  return (
    <As
      className={cx(
        'rounded-[--radius-card] border border-stone-200 bg-white p-4 shadow-sm',
        className,
      )}
    >
      {children}
    </As>
  )
}

// --- Money ------------------------------------------------------------------

/**
 * Renders an amount with tabular figures so stacked amounts align.
 *
 * Takes paise and formats at the edge — the one place rupees are allowed to
 * exist. Anything passing a float here is a bug the type signature catches.
 */
export function Money({
  paise,
  className,
  alwaysShowPaise,
}: {
  paise: Paise
  className?: string
  alwaysShowPaise?: boolean
}) {
  return (
    <span className={cx('numeric', className)}>
      {formatINR(paise, { alwaysShowPaise })}
    </span>
  )
}

// --- Stat tile --------------------------------------------------------------

export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  onClick,
}: {
  label: string
  value: ReactNode
  hint?: string
  tone?: 'neutral' | 'positive' | 'warning'
  onClick?: () => void
}) {
  const toneStyles = {
    neutral: 'text-stone-900',
    positive: 'text-brand-700',
    warning: 'text-amber-700',
  }[tone]

  const content = (
    <Card
      className={cx(
        'flex min-w-0 flex-col gap-1',
        onClick && 'transition-colors hover:bg-stone-50',
      )}
    >
      <span className="text-xs font-medium tracking-wide text-stone-500 uppercase">
        {label}
      </span>
      <span
        className={cx(
          'numeric text-lg font-bold tracking-tight min-[400px]:text-2xl',
          toneStyles,
        )}
      >
        {value}
      </span>
      {hint && <span className="text-xs text-stone-500">{hint}</span>}
    </Card>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="tap-target block w-full min-w-0 text-left">
        {content}
      </button>
    )
  }

  return content
}

// --- Status chip ------------------------------------------------------------

export type ChipTone = 'neutral' | 'active' | 'done' | 'late' | 'draft'

const chipStyles: Record<ChipTone, string> = {
  neutral: 'bg-stone-100 text-stone-700',
  active: 'bg-brand-100 text-brand-800',
  done: 'bg-emerald-100 text-emerald-800',
  late: 'bg-red-100 text-red-800',
  draft: 'bg-amber-100 text-amber-800',
}

/**
 * Status is carried by a text label, not colour alone — colour-blind users and
 * anyone reading a printed schedule need the word.
 */
export function StatusChip({ label, tone = 'neutral' }: { label: string; tone?: ChipTone }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
        chipStyles[tone],
      )}
    >
      {label}
    </span>
  )
}

// --- Form field -------------------------------------------------------------

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hint?: string
  error?: string
}

/**
 * A labelled input wired for accessibility: the label is associated by id, and
 * errors are linked via aria-describedby and announced, rather than being red
 * text a screen reader never reaches.
 */
export function Field({ label, hint, error, className, id, ...rest }: FieldProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = hint ? `${fieldId}-hint` : undefined
  const errorId = error ? `${fieldId}-error` : undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium text-stone-700">
        {label}
      </label>
      <input
        {...rest}
        id={fieldId}
        aria-invalid={error ? true : undefined}
        aria-describedby={cx(hintId, errorId) || undefined}
        className={cx(
          'tap-target rounded-xl border bg-white px-4 py-3 text-base',
          'placeholder:text-stone-400',
          error ? 'border-red-500' : 'border-stone-300',
          className,
        )}
      />
      {hint && !error && (
        <span id={hintId} className="text-xs text-stone-500">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" className="text-xs font-medium text-red-700">
          {error}
        </span>
      )}
    </div>
  )
}

/** A labelled number input with ± buttons — easier on a phone than typing digits. */
export function NumberStepper({
  label,
  hint,
  error,
  value,
  min,
  max,
  onChange,
  required,
}: {
  label: string
  hint?: string
  error?: string
  value: number
  min: number
  max: number
  onChange: (value: number) => void
  required?: boolean
}) {
  const autoId = useId()
  const hintId = hint ? `${autoId}-hint` : undefined
  const errorId = error ? `${autoId}-error` : undefined

  function setValue(raw: number) {
    if (!Number.isFinite(raw)) return
    onChange(Math.min(max, Math.max(min, Math.round(raw))))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span id={`${autoId}-label`} className="text-sm font-medium text-stone-700">
        {label}
      </span>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          aria-label="Decrease"
          disabled={value <= min}
          onClick={() => setValue(value - 1)}
          className={cx(
            'tap-target flex w-14 shrink-0 items-center justify-center rounded-xl border',
            'border-stone-300 bg-white text-xl font-semibold text-stone-700',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          −
        </button>
        <input
          id={autoId}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          required={required}
          aria-labelledby={`${autoId}-label`}
          aria-invalid={error ? true : undefined}
          aria-describedby={cx(hintId, errorId) || undefined}
          onChange={(e) => setValue(Number(e.target.value))}
          className={cx(
            'tap-target numeric flex-1 rounded-xl border bg-white px-4 py-3 text-center text-base font-semibold',
            error ? 'border-red-500' : 'border-stone-300',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          )}
        />
        <button
          type="button"
          aria-label="Increase"
          disabled={value >= max}
          onClick={() => setValue(value + 1)}
          className={cx(
            'tap-target flex w-14 shrink-0 items-center justify-center rounded-xl border',
            'border-stone-300 bg-white text-xl font-semibold text-stone-700',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          +
        </button>
      </div>
      {hint && !error && (
        <span id={hintId} className="text-xs text-stone-500">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" className="text-xs font-medium text-red-700">
          {error}
        </span>
      )}
    </div>
  )
}

// --- Choice controls --------------------------------------------------------

export interface SelectOption {
  value: string
  label: string
}

export interface SelectFieldProps {
  label: string
  hint?: string
  error?: string
  value: string
  options: readonly SelectOption[]
  /** Shown as a disabled first option while nothing is chosen. */
  placeholder?: string
  onChange: (value: string) => void
}

/**
 * A single choice, as a native select.
 *
 * Native deliberately, not a styled listbox. On Android this opens the system picker and
 * on iOS the wheel, both of which are large, scrollable with a thumb, and already
 * familiar — and this is a public form filled in on a cheap phone, often by somebody who
 * does not use apps much. A hand-built dropdown would look more consistent with the rest
 * of the app and be materially harder to actually use.
 *
 * The arrow is drawn rather than left to the browser, because the default control chrome
 * varies enough between Android versions to look broken beside the other fields.
 */
export function SelectField({
  label,
  hint,
  error,
  value,
  options,
  placeholder,
  onChange,
}: SelectFieldProps) {
  const autoId = useId()
  const hintId = hint ? autoId + '-hint' : undefined
  const errorId = error ? autoId + '-error' : undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={autoId} className="text-sm font-medium text-stone-700">
        {label}
      </label>
      <div className="relative">
        <select
          id={autoId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={cx(hintId, errorId) || undefined}
          className={cx(
            'tap-target w-full appearance-none rounded-xl border bg-white px-4 py-3',
            'pr-11 text-base',
            error ? 'border-red-500' : 'border-stone-300',
            value ? 'text-stone-900' : 'text-stone-400',
          )}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} className="text-stone-900">
              {option.label}
            </option>
          ))}
        </select>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-stone-400"
        >
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {hint && !error && (
        <span id={hintId} className="text-xs text-stone-500">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" className="text-xs font-medium text-red-700">
          {error}
        </span>
      )}
    </div>
  )
}

export interface MultiSelectProps {
  label: string
  hint?: string
  error?: string
  values: readonly string[]
  options: readonly SelectOption[]
  /** Shown on the closed control when nothing is chosen. */
  placeholder: string
  onChange: (values: string[]) => void
}

/**
 * Several choices, as a dropdown of checkboxes.
 *
 * A native multiple select would be the obvious counterpart to SelectField, and it is
 * unusable on a phone: it renders as a short scrolling box, and choosing more than one
 * entry needs modifier keys a touch screen does not have. So this keeps the dropdown
 * shape — a closed control that summarises, tapped to reveal the options — with
 * checkboxes inside, which is the one multi-select idiom that works with a thumb.
 *
 * The closed control lists what is chosen rather than counting it ("Chain, Bangles", not
 * "2 selected"), because on a small phone that summary is often the only thing the
 * applicant sees of their own answer before submitting.
 *
 * It closes on a tap outside and on Escape, and deliberately NOT on each choice — the
 * whole point is picking several.
 */
export function MultiSelect({
  label,
  hint,
  error,
  values,
  options,
  placeholder,
  onChange,
}: MultiSelectProps) {
  const autoId = useId()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent | TouchEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const chosen = options.filter((option) => values.includes(option.value))
  const summary = chosen.length > 0 ? chosen.map((o) => o.label).join(', ') : placeholder
  const labelId = autoId + '-label'

  function toggle(value: string) {
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : // Kept in the published option order rather than tap order, so one selection
          // always reads the same way whichever order the boxes were ticked.
          options
            .filter((o) => o.value === value || values.includes(o.value))
            .map((o) => o.value),
    )
  }

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <span id={labelId} className="text-sm font-medium text-stone-700">
        {label}
      </span>
      <div className="relative">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={labelId}
          aria-invalid={error ? true : undefined}
          onClick={() => setOpen((o) => !o)}
          className={cx(
            'tap-target flex w-full items-center gap-2 rounded-xl border bg-white',
            'px-4 py-3 text-left text-base',
            error ? 'border-red-500' : 'border-stone-300',
            chosen.length > 0 ? 'text-stone-900' : 'text-stone-400',
          )}
        >
          <span className="min-w-0 flex-1">{summary}</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={cx(
              'size-5 shrink-0 text-stone-400 transition-transform',
              open ? 'rotate-180' : '',
            )}
          >
            <path
              d="m6 9 6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {open && (
          <div
            role="listbox"
            aria-multiselectable
            aria-labelledby={labelId}
            // Capped height with scrolling: on a small phone with the keyboard up the
            // full list runs off the bottom of the screen, which hides Other — the one
            // option somebody with unusual jewellery actually needs.
            className={cx(
              'absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto',
              'rounded-xl border border-stone-300 bg-white py-1 shadow-lg',
            )}
          >
            {options.map((option) => {
              const checked = values.includes(option.value)
              return (
                <label
                  key={option.value}
                  role="option"
                  aria-selected={checked}
                  className="tap-target flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-stone-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(option.value)}
                    className="size-5 shrink-0"
                  />
                  <span className="text-base text-stone-900">{option.label}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>
      {hint && !error && <span className="text-xs text-stone-500">{hint}</span>}
      {error && (
        <span role="alert" className="text-xs font-medium text-red-700">
          {error}
        </span>
      )}
    </div>
  )
}

// --- Disclosure -------------------------------------------------------------

/**
 * The affordance on a row that opens.
 *
 * Points right when closed and down when open. Used by the loan register, the
 * member list and the settings hub, which is why it lives here — three copies of
 * the same arrow drifting apart is how a list stops feeling like one app.
 *
 * Purely decorative: the state that matters is `aria-expanded` on the control
 * itself, so this is hidden from assistive technology.
 */
export function Chevron({ open = false }: { open?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={[
        'size-5 shrink-0 text-stone-400 transition-transform',
        open ? 'rotate-90' : '',
      ].join(' ')}
    >
      <path
        d="m9 6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Progress ---------------------------------------------------------------

/** Repayment progress. Announced as a percentage for screen readers. */
export function ProgressBar({
  paidPaise,
  totalPaise,
  label,
}: {
  paidPaise: Paise
  totalPaise: Paise
  label?: string
}) {
  const pct = totalPaise > 0 ? Math.min(100, Math.round((paidPaise / totalPaise) * 100)) : 0
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Repayment progress'}
      className="h-2 w-full overflow-hidden rounded-full bg-stone-200"
    >
      <div
        className="h-full rounded-full bg-brand-600 transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

// --- Feedback ---------------------------------------------------------------

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-base font-semibold text-stone-700">{title}</p>
      {body && <p className="text-sm text-stone-500">{body}</p>}
    </div>
  )
}

export function ErrorNotice({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      {message}
    </div>
  )
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-12 text-stone-500">
      <span
        aria-hidden="true"
        className="size-5 animate-spin rounded-full border-2 border-stone-300 border-t-brand-600"
      />
      <span className="text-sm">{label}</span>
    </div>
  )
}
